import { html, render, RootPart, TemplateResult } from 'lit';
import { Constructor } from 'lit-element';
import { property, state } from 'lit/decorators.js';
import { GridItemModel } from '@vaadin/vaadin-grid';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { GzDataTableWebComponent } from '../component/GzDataTableWebComponent';
import { DataTableEvents, DefaultColumnKeys, IDataTableColumn } from '../types';
import { ButtonColor, ButtonType } from '../../gzIconButton/types';
import { FontSizes } from '../../../types/common/types';

// all public attributes and methods from the decorator class should be exposed in the interface
export interface RowDeletionInterface {
  allowRowDeletion: boolean;
  deleteItem(rowIndex: number): void;
}

// all protected attributes and methods from the decorator class, used by other decorators, should be exposed here
export interface RowDeletionProtectedInterface {
  rowDeletionDisabled: boolean;
}

export const rowDeletion = <T extends Constructor<GzDataTableWebComponent<TDataTableItem>>, TDataTableItem = unknown>(
  dataTableClass: T
) => {
  class RowDeletionDecorator extends dataTableClass {
    /**
     * If set, it adds on each row a delete button on the <code>DefaultColumnKeys.ACTIONS</code> column, which allows
     * deleting the row
     */
    @property({ type: Boolean, attribute: true, reflect: true })
    allowRowDeletion = false;

    @state()
    protected rowDeletionDisabled = false;

    deleteItem(rowIndex: number): void {
      const item = this.currentItems[rowIndex];
      this.currentItems = this.currentItems.filter((_, index) => index !== rowIndex);
      this.dispatchEvent(
        new CustomEvent(DataTableEvents.ROW_DELETED, {
          detail: { item },
          bubbles: true,
          composed: true,
        })
      );
    }

    protected renderColumn(column: IDataTableColumn, index: number): GridColumnElement {
      // overridden function
      const columnElement = super.renderColumn(column, index);
      if (this.allowRowDeletion && column.key === DefaultColumnKeys.ACTIONS) {
        Object.assign(columnElement, {
          resizable: false,
          flexGrow: 0,
        });
      }
      return columnElement;
    }

    protected renderColumnCell(
      root: HTMLElement,
      rowData: GridItemModel | undefined,
      column: IDataTableColumn
    ): RootPart | undefined {
      // overridden function
      if (!rowData) {
        return;
      }

      if (this.allowRowDeletion && column.key === DefaultColumnKeys.ACTIONS) {
        return render(
          html`
            <div class="actions-cell">${this.renderColumnCellElements('', column, rowData.index).map(e => e)}</div>
          `,
          root
        );
      }
      return super.renderColumnCell(root, rowData, column);
    }

    protected renderColumnCellElements(content: unknown, column: IDataTableColumn, rowIndex: number): TemplateResult[] {
      // overridden function

      if (this.allowRowDeletion && column.key === DefaultColumnKeys.ACTIONS) {
        return [
          ...super.renderColumnCellElements(content, column, rowIndex),
          this.instance.editingRowIndex === rowIndex ? html`` : this.renderDeleteIcon(rowIndex),
        ];
      }
      return super.renderColumnCellElements(content, column, rowIndex);
    }

    protected renderDeleteIcon(rowIndex: number): TemplateResult {
      return html`
      <gz-tooltip tooltipText="${
        !this.rowDeletionDisabled ? this.strings.deleteRowTooltip : ''
      }" class="action-btn-tooltip">
        <gz-icon-button
          icon="ds-icon-cancel"
          fontSize="${FontSizes.HEADING_3}"
          class="action-icon"
          ?disabled="${this.rowDeletionDisabled}"
          buttonType="${ButtonType.SQUARE}"
          buttonColor="${ButtonColor.SECONDARY}"
          @click="${() => {
            this.deleteItem(rowIndex);
          }}"
        ></gz-icon-button></<gz-tooltip>
      `;
    }
  }

  return RowDeletionDecorator;
};
